/** GERP 협력사 평가(127) — 레거시 appr_edit_forms_product_result_mstr */
export const SUPPLIER_EVALUATION_TYPE = 127;

export const isSupplierEvaluationType = (apvType: number): boolean =>
  apvType === SUPPLIER_EVALUATION_TYPE;

/** 레거시 save_doc_product_result_mstr 알럿 — 임시저장·상신 모두 */
export const SUPPLIER_EVALUATION_MISSING_MESSAGE =
  '데이터를 로드하지 않았습니다. 조회 조건 선택 후 확인 버튼을 누르세요.';

/** 레거시 btn_apv_eva — 고른 건 없이 「확인」 */
export const SUPPLIER_EVALUATION_EMPTY_MESSAGE = '내역이 없습니다.';

/** GERP 미상신 결재 건 — 키는 GERP 응답 그대로 */
export type SupplierEvaluationCandidate = {
  sysId: string;
  aprvId: string;
  aprvTypcd: string;
  title?: string | null;
  docTit?: string | null;
};

type Value = string | number | null | undefined;

/** 평가 행 — 키는 GERP 응답 그대로 */
export type SupplierEvaluationRow = {
  evalTypNm?: Value;
  vdNm?: Value;
  erpVdCd?: Value;
  vdCd?: Value;
  evalSco?: Value;
  sgRank?: Value;
  poId?: Value;
  itemQty?: Value;
  styleTotPrice?: Value;
  estBuyPrc?: Value;
  dstrRt?: Value;
};

export type SupplierEvaluationContent = {
  aprvId?: Value;
  appId?: Value;
  detailMenuId?: Value;
  detailUrl?: Value;
  rows?: SupplierEvaluationRow[] | null;
};

export type SupplierEvaluationDraftSource = {
  code: number;
  attributes: Record<string, unknown>;
  display: {
    candidates: SupplierEvaluationCandidate[] | null;
    selection: string[] | null;
    subject: string | null;
    content: SupplierEvaluationContent | null;
  };
};

/** 저장 attributes 와 같은 모양 */
export type SupplierEvaluationState = {
  systemId: string;
  approvalTypeCode: string;
  approvalId: string;
  /** 작성 시점 본문 HTML */
  content: string | null;
};

const text = (v: unknown): string | null =>
  typeof v === 'string' && v.trim() ? v : null;

export const parseSupplierEvaluationAttributes = (
  attributes: Record<string, unknown> | null | undefined,
): SupplierEvaluationState | null => {
  const systemId = text(attributes?.systemId);
  const approvalTypeCode = text(attributes?.approvalTypeCode);
  const approvalId = text(attributes?.approvalId);
  if (!attributes || !systemId || !approvalTypeCode || !approvalId) return null;
  return {
    systemId,
    approvalTypeCode,
    approvalId,
    content: text(attributes.content),
  };
};

export const parseSupplierEvaluationContent = (
  attributes: Record<string, unknown> | null | undefined,
): string | null => text(attributes?.content);

export const buildSupplierEvaluationAttributes = (
  state: SupplierEvaluationState | null | undefined,
) =>
  state
    ? {
        systemId: state.systemId,
        approvalTypeCode: state.approvalTypeCode,
        approvalId: state.approvalId,
        content: state.content,
      }
    : null;

export const checkSupplierEvaluationBeforeSave = (
  state: SupplierEvaluationState | null | undefined,
): string | null =>
  state?.content ? null : SUPPLIER_EVALUATION_MISSING_MESSAGE;

export const supplierEvaluationKey = (
  state: SupplierEvaluationState | null | undefined,
) => state?.approvalId ?? '';

/** 레거시 콤보 dataTextField=DOC_TIT. 「확인」 때 이 글자가 제목이 됨 */
export const buildSupplierEvaluationCandidateLabel = (
  c: SupplierEvaluationCandidate,
) => c.docTit?.trim() || c.title?.trim() || c.aprvId;

const ERP_SSO_URL = 'https://erp.fnf.co.kr/ssoLoginProcess.do';
const DETAIL_MENU_ID = 'SRM31500';

/** 저장 HTML 값이라 ERP SSO 상세 화면(https, fnf.co.kr)만 염 */
export const isSupplierEvaluationDetailUrl = (
  url: string | null | undefined,
) => {
  if (!url) return false;
  try {
    const parsed = new URL(url);
    return (
      parsed.protocol === 'https:' &&
      (parsed.hostname === 'fnf.co.kr' ||
        parsed.hostname.endsWith('.fnf.co.kr')) &&
      parsed.pathname === '/ssoLoginProcess.do'
    );
  } catch {
    return false;
  }
};

/**
 * 레거시처럼 보는 사람 ID로 ERP 에 들어감. 저장 주소는 작성자 ID라 username 만 바꿈.
 * 저장 주소가 없으면 평가 ID(appId)로, 레거시 문서는 결재 건 ID 로 주소를 만듦
 */
export const buildSupplierEvaluationDetailUrl = ({
  detailUrl,
  appId,
  legacyApprovalId,
  viewerId,
}: {
  detailUrl?: string | null;
  appId?: string | null;
  legacyApprovalId?: string | null;
  viewerId?: string | null;
}): string | null => {
  const username = viewerId?.trim().toUpperCase();
  if (!username) return null;
  if (detailUrl) {
    if (!isSupplierEvaluationDetailUrl(detailUrl)) return null;
    const url = new URL(detailUrl);
    url.searchParams.set('username', username);
    return url.toString();
  }
  const id = appId || legacyApprovalId;
  if (!id) return null;
  const query = new URLSearchParams({
    menuId: DETAIL_MENU_ID,
    username,
    app_id: id,
  });
  return `${ERP_SSO_URL}?${query.toString()}`;
};

/** 레거시 apvdetail — 전체 화면 크기 새 창 */
export const openSupplierEvaluationDetail = (url: string) => {
  window.open(
    url,
    '_blank',
    `noopener,noreferrer,height=${window.screen.height},width=${window.screen.width}`,
  );
};
